// TelcoTrack — moves a list table's filter/export cluster onto the page-header
// subtitle row (`.tt-page-header__table-tools`), beside the subtitle.
// One slot, several sources. A table inside a dialog stays put. An export-only
// toolbar stays with its table once a filter toolbar is already on the subtitle row,
// so the header does not collect a second Export.

import { Injectable } from '@angular/core';

function insideDialog(el: HTMLElement): boolean {
  return !!el.closest('app-modal, [role="dialog"]');
}

function hasFilterContent(el: HTMLElement): boolean {
  const filters = el.querySelector('.tt-data-table__filters');
  return !!filters && filters.childElementCount > 0;
}

function hasExport(el: HTMLElement): boolean {
  return !!el.querySelector('.tt-data-table__export');
}

@Injectable({ providedIn: 'root' })
export class PageHeaderTableToolsService {
  private readonly slots: HTMLElement[] = [];
  private readonly sources: HTMLElement[] = [];
  private readonly placeholders = new Map<HTMLElement, Comment>();

  private get slot(): HTMLElement | null {
    return this.slots[this.slots.length - 1] ?? null;
  }

  registerSlot(slot: HTMLElement): void {
    const existing = this.slots.indexOf(slot);
    if (existing >= 0) this.slots.splice(existing, 1);
    for (const source of [...this.sources]) this.restore(source);
    this.slots.push(slot);
    for (const source of this.sources) this.park(source);
  }

  unregisterSlot(slot: HTMLElement): void {
    const idx = this.slots.indexOf(slot);
    if (idx < 0) return;
    const wasActive = idx === this.slots.length - 1;
    if (wasActive) {
      for (const source of [...this.sources]) this.restore(source);
    }
    this.slots.splice(idx, 1);
    if (wasActive && this.slot) {
      for (const source of this.sources) this.park(source);
    }
  }

  attach(source: HTMLElement): void {
    if (this.sources.includes(source)) return;
    if (insideDialog(source)) return;
    const filters = hasFilterContent(source);
    const exportButton = hasExport(source);
    if (!filters && !exportButton) return;
    // A second export-only toolbar stays on its table (month sections, pending invites).
    if (!filters && this.sources.length > 0) return;

    this.sources.push(source);
    if (filters) {
      for (const other of [...this.sources]) {
        if (other !== source && !hasFilterContent(other)) this.detach(other);
      }
    }
    this.park(source);
  }

  detach(source: HTMLElement): void {
    const index = this.sources.indexOf(source);
    if (index < 0) return;
    this.restore(source);
    this.sources.splice(index, 1);
  }

  private park(source: HTMLElement): void {
    if (!this.slot || source.parentElement === this.slot) return;
    if (this.placeholders.has(source)) return;
    const placeholder = document.createComment('tt-page-header-table-tools');
    source.parentNode?.insertBefore(placeholder, source);
    this.placeholders.set(source, placeholder);
    this.slot.appendChild(source);
    markSubtitleRow(this.slot);
  }

  private restore(source: HTMLElement): void {
    const slot = source.parentElement;
    const placeholder = this.placeholders.get(source);
    this.placeholders.delete(source);
    if (!placeholder?.parentNode) return;
    placeholder.parentNode.insertBefore(source, placeholder);
    placeholder.remove();
    if (slot) clearSubtitleRow(slot);
  }
}

function markSubtitleRow(slot: HTMLElement): void {
  slot.closest('.tt-page-header__subtitle-row')?.classList.add('tt-page-header__subtitle-row--live');
}

function clearSubtitleRow(slot: HTMLElement): void {
  const row = slot.closest('.tt-page-header__subtitle-row');
  if (!row) return;
  const tools = row.querySelector('.tt-page-header__table-tools');
  const filters = row.querySelector('.tt-page-header__filters');
  const occupied = (tools?.childElementCount ?? 0) > 0 || (filters?.childElementCount ?? 0) > 0;
  if (!occupied) row.classList.remove('tt-page-header__subtitle-row--live');
}
